import { useState, useEffect, useRef, useMemo } from 'react';
import type { CartResponse } from '@/features/cart/types';
import { CartStatus } from '@/features/cart/types';
import { formatTimeRemaining } from '@/features/cart/utils/timerUtils';

export interface UsePaymentTimerOptions {
  onExpire?: () => void;
}

export interface UsePaymentTimerReturn {
  timeRemaining: number | null;
  isExpired: boolean;
  formattedTime: string;
  /** True if the timer was already expired when the hook first mounted/calculated */
  wasExpiredOnMount: boolean;
}

export function usePaymentTimer(
  cart: CartResponse | null,
  options?: UsePaymentTimerOptions
): UsePaymentTimerReturn {
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Track if timer was expired on initial mount
  const wasExpiredOnMountRef = useRef<boolean | null>(null);
  const [wasExpiredOnMount, setWasExpiredOnMount] = useState(false);

  // Track previous isExpired state to detect transitions
  const prevIsExpiredRef = useRef<boolean>(false);

  // Track if onExpire has been called to prevent duplicate calls
  const hasCalledOnExpireRef = useRef(false);

  // Store onExpire in a ref to avoid dependency issues
  const onExpireRef = useRef(options?.onExpire);
  onExpireRef.current = options?.onExpire;

  // Timer effect - update time remaining based on payment_intent_expires_at
  // Run timer when cart status is PENDING_PAYMENT or FAILED_PAYMENT
  useEffect(() => {
    // Run timer for PENDING_PAYMENT or FAILED_PAYMENT status
    const shouldRunTimer =
      (cart?.status === CartStatus.PendingPayment || cart?.status === CartStatus.FailedPayment) &&
      cart?.payment_intent_expires_at;

    if (!shouldRunTimer) {
      setTimeRemaining(null);
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      return;
    }

    const updateTimer = () => {
      const expiresAt = new Date(cart.payment_intent_expires_at!).getTime();
      const now = new Date().getTime();
      const remaining = Math.max(0, Math.floor((expiresAt - now) / 1000));
      setTimeRemaining(remaining);

      if (remaining <= 0) {
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
      }
    };

    updateTimer();
    timerIntervalRef.current = setInterval(updateTimer, 1000);

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [cart?.payment_intent_expires_at, cart?.status]);

  const isExpired = useMemo(() => {
    const validStatus = cart?.status === CartStatus.PendingPayment || cart?.status === CartStatus.FailedPayment;
    return validStatus && timeRemaining !== null && timeRemaining <= 0;
  }, [cart?.status, timeRemaining]);

  // Detect if timer was expired on mount (first calculation)
  useEffect(() => {
    // Only set once on first valid calculation
    if (wasExpiredOnMountRef.current === null && timeRemaining !== null) {
      const expiredOnMount = timeRemaining <= 0;
      wasExpiredOnMountRef.current = expiredOnMount;
      setWasExpiredOnMount(expiredOnMount);

      // If already expired on mount, mark that we shouldn't call onExpire
      if (expiredOnMount) {
        hasCalledOnExpireRef.current = true;
      }
    }
  }, [timeRemaining]);

  // Detect transition from non-expired to expired and fire callback
  useEffect(() => {
    // Check for transition: was not expired before, is expired now
    if (!prevIsExpiredRef.current && isExpired && !hasCalledOnExpireRef.current) {
      hasCalledOnExpireRef.current = true;
      onExpireRef.current?.();
    }

    // Update previous state
    prevIsExpiredRef.current = isExpired;
  }, [isExpired]);

  const formattedTime = useMemo(() => {
    return timeRemaining !== null ? formatTimeRemaining(timeRemaining) : '--:--';
  }, [timeRemaining]);

  return {
    timeRemaining,
    isExpired,
    formattedTime,
    wasExpiredOnMount,
  };
}

